import * as NextSkyTheme from "@NextSkyTheme/global";
import { eventModal } from "@NextSkyTheme/modal";

const AVAILABILITY_API_DEFAULT =
  "https://apps.medicaloutfit.com/getAvailability";

// Cache store directory JSON for the page lifetime (keyed by URL / locale)
let locationsCache = null;
let locationsCacheUrl = null;
// Variants list rarely changes on PDP; keep last parsed copy across block re-renders
let variantsCache = null;
// Survives block-product__pickup innerHTML swaps after section re-fetch
let loadedAvailabilityVariantId = null;
let inFlightAvailabilityVariantId = null;

function parseJsonResponse(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function flattenStores(locationsByCity) {
  if (!locationsByCity || typeof locationsByCity !== "object") return [];
  return Object.values(locationsByCity).flat().filter(Boolean);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getMainProductScope(host) {
  return (
    host?.closest(".shopify-section") ||
    document.querySelector(".sec__main-product")?.closest(".shopify-section") ||
    document.querySelector(".sec__main-product") ||
    document
  );
}

function getMainVariantInput(scope) {
  return (
    scope.querySelector("variant-input.product-variants-js") ||
    scope.querySelector(".product-detail__information variant-input") ||
    scope.querySelector("variant-input")
  );
}

/** Live option labels from checked swatches — updates immediately on click */
function getLiveVariantOptions(scope) {
  const variantInput = getMainVariantInput(scope);
  if (!variantInput) return [];

  const fieldsets = variantInput.querySelectorAll(
    ".product-card-variant-js, .product-bundle-swatch-js, fieldset.product-variants__input",
  );
  const options = [];

  fieldsets.forEach((fieldset) => {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    const nameEl = fieldset.querySelector(".heading-style");
    const valueEl =
      fieldset.querySelector(".product-variants-info .option_value") ||
      fieldset.querySelector("legend .option_value") ||
      fieldset.querySelector(".option_value");

    const name = (nameEl?.textContent || "")
      .replace(/:\s*$/, "")
      .trim();
    const value = (
      checked?.getAttribute("data-option-value") ||
      valueEl?.textContent ||
      ""
    ).trim();

    if (name && value) options.push({ name, value });
  });

  return options;
}

/** Resolve variant id from currently checked option values — no section wait */
function resolveVariantIdFromSwatches(scope, variants) {
  if (!Array.isArray(variants) || !variants.length) return null;

  const variantInput = getMainVariantInput(scope);
  if (!variantInput) return null;

  const checked = Array.from(
    variantInput.querySelectorAll('input[type="radio"]:checked'),
  );
  if (!checked.length) return null;

  checked.sort(
    (a, b) =>
      Number(a.getAttribute("data-option-position") || 0) -
      Number(b.getAttribute("data-option-position") || 0),
  );

  const selectedValues = checked.map((input) =>
    (input.getAttribute("data-option-value") || "").trim(),
  );
  if (!selectedValues.length || selectedValues.some((v) => !v)) return null;

  const match = variants.find((variant) => {
    const options = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter(
          (v) => v != null && v !== "",
        );
    if (options.length !== selectedValues.length) return false;
    return selectedValues.every((value, index) => options[index] === value);
  });

  return match?.id ? String(match.id) : null;
}

async function fetchAvailabilityMap(apiUrl, variantId) {
  const url = `${apiUrl}${apiUrl.includes("?") ? "&" : "?"}variantId=${encodeURIComponent(variantId)}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Availability API ${response.status}`);
  const raw = await response.text();
  const data = parseJsonResponse(raw);
  if (!data) throw new Error("Invalid availability payload");
  return data;
}

async function fetchLocations(locationsUrl) {
  if (locationsCache && locationsCacheUrl === locationsUrl) {
    return locationsCache;
  }
  const response = await fetch(locationsUrl);
  if (!response.ok) throw new Error(`Locations JSON ${response.status}`);
  const data = await response.json();
  locationsCache = data;
  locationsCacheUrl = locationsUrl;
  return data;
}

function buildStoreRows(stores, availabilityMap) {
  const rows = stores.map((store) => {
    const locationId = store.locationId;
    const qty = Number(availabilityMap?.[locationId] ?? 0);
    return {
      name: store.name || "",
      address: store.address || "",
      googlelocation: store.googlelocation || "",
      locationId,
      qty: Number.isFinite(qty) ? qty : 0,
      available: Number.isFinite(qty) ? qty > 0 : false,
    };
  });

  // In-stock first, then out of stock; keep stable name order within groups
  rows.sort((a, b) => {
    if (a.available !== b.available) return a.available ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return rows;
}

if (!customElements.get("pickup-availability")) {
  customElements.define(
    "pickup-availability",
    class PickupAvailability extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute("available")) return;
        this.drawer = this.ensureDrawerOnBody();
        this.readVariantsCache();
        this.bindVariantChangeListener();

        // Warm locations JSON as soon as the block mounts (shared cache)
        const locationsUrl = this.dataset.locationsUrl;
        if (locationsUrl) {
          fetchLocations(locationsUrl).catch(() => {});
        }

        // Load store stock on first paint — do not wait for drawer open
        this.refreshForCurrentVariant();
      }

      disconnectedCallback() {
        this.unbindVariantChangeListener();
      }

      readVariantsCache() {
        const script = this.querySelector("[data-product-variants]");
        if (!script) return variantsCache || [];
        try {
          const parsed = JSON.parse(script.textContent || "[]");
          if (Array.isArray(parsed) && parsed.length) {
            variantsCache = parsed;
          }
        } catch (e) {
          /* keep previous cache */
        }
        return variantsCache || [];
      }

      getSelectedVariantId() {
        const scope = getMainProductScope(this);
        const variants = this.readVariantsCache();

        // 1) Swatches update immediately on click — do not wait for section HTML
        const fromSwatches = resolveVariantIdFromSwatches(scope, variants);
        if (fromSwatches) return fromSwatches;

        // 2) Form id (updated after section fetch)
        const input = scope.querySelector(".product-variant-id, [name='id']");
        if (input && input.value) return input.value;

        // 3) URL param
        const urlVariant = new URLSearchParams(window.location.search).get(
          "variant",
        );
        if (urlVariant) return urlVariant;

        // 4) SSR fallback from Liquid
        return this.dataset.variantId;
      }

      /** Fetch for the live selected variant; skip if already loaded/in-flight */
      refreshForCurrentVariant({ force = false } = {}) {
        const variantId = this.getSelectedVariantId();
        if (!variantId) return;
        this.refresh(variantId, { force });
      }

      bindVariantChangeListener() {
        if (this._onVariantInputChange) return;

        // Capture change on the product scope so stock reloads as soon as a
        // swatch is clicked — before section HTML re-render finishes.
        this._onVariantInputChange = (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement)) return;
          if (target.type !== "radio") return;
          if (!target.closest("variant-input")) return;

          const scope = getMainProductScope(this);
          if (!scope.contains(target)) return;

          this.refreshForCurrentVariant();
        };

        document.addEventListener("change", this._onVariantInputChange, true);
      }

      unbindVariantChangeListener() {
        if (!this._onVariantInputChange) return;
        document.removeEventListener(
          "change",
          this._onVariantInputChange,
          true,
        );
        this._onVariantInputChange = null;
      }

      ensureDrawerOnBody() {
        const host =
          this.closest(".block-product__pickup") || this.parentElement;
        const fromBlock = host?.querySelector("pickup-availability-drawer");
        const onBody = document.body.querySelector(
          ":scope > pickup-availability-drawer",
        );

        if (onBody && fromBlock && onBody !== fromBlock) {
          // Variant re-render brought a fresh shell — keep the live drawer on body
          fromBlock.remove();
          return onBody;
        }
        if (fromBlock) {
          document.body.appendChild(fromBlock);
          return fromBlock;
        }
        return onBody;
      }

      async refresh(variantId, { force = false } = {}) {
        if (!variantId || !this.drawer) return;

        const variantKey = String(variantId);
        if (
          !force &&
          (variantKey === loadedAvailabilityVariantId ||
            variantKey === inFlightAvailabilityVariantId)
        ) {
          this.updateHeader();
          this.enablePromoButton(true);
          return;
        }

        const apiUrl = this.dataset.apiUrl || AVAILABILITY_API_DEFAULT;
        const locationsUrl = this.dataset.locationsUrl;
        if (!locationsUrl) return;

        // Ignore stale responses when variant changes / drawer re-opens quickly
        this._requestToken = (this._requestToken || 0) + 1;
        const requestToken = this._requestToken;
        inFlightAvailabilityVariantId = variantKey;

        this.updateHeader();
        this.setLoading(true);
        this.setError(null);

        try {
          const [availabilityMap, locationsByCity] = await Promise.all([
            fetchAvailabilityMap(apiUrl, variantId),
            fetchLocations(locationsUrl),
          ]);
          if (requestToken !== this._requestToken) return;

          const stores = flattenStores(locationsByCity);
          const rows = buildStoreRows(stores, availabilityMap);
          this.renderList(rows);
          loadedAvailabilityVariantId = variantKey;
          this.enablePromoButton(true);
        } catch (e) {
          if (requestToken !== this._requestToken) return;
          console.error("[pickup-availability]", e);
          this.renderList([]);
          loadedAvailabilityVariantId = null;
          this.setError(this.dataset.labelError || "Couldn't load availability");
          this.enablePromoButton(true);
        } finally {
          if (requestToken === this._requestToken) {
            inFlightAvailabilityVariantId = null;
            this.setLoading(false);
          }
        }
      }

      updateHeader() {
        if (!this.drawer) return;
        const titleEl = this.drawer.querySelector("[data-availability-title]");
        if (titleEl && this.dataset.productTitle) {
          titleEl.textContent = this.dataset.productTitle;
        }

        const variantEl = this.drawer.querySelector(
          "[data-availability-variant]",
        );
        if (!variantEl) return;

        // Prefer live swatch labels so the header matches the click immediately
        let options = getLiveVariantOptions(getMainProductScope(this));

        if (!options.length) {
          const script = this.querySelector("[data-variant-options]");
          if (script) {
            try {
              options = JSON.parse(script.textContent || "[]") || [];
            } catch (e) {
              options = [];
            }
          }
        }

        if (!options.length) {
          variantEl.innerHTML = "";
          return;
        }

        variantEl.innerHTML = options
          .map(
            (opt) =>
              `<p class="pickup-availability-variant__line my-0">${escapeHtml(opt.name)}:&nbsp;<span>${escapeHtml(opt.value)}</span></p>`,
          )
          .join("");
      }

      setLoading(isLoading) {
        if (!this.drawer) return;
        const loader = this.drawer.querySelector("[data-availability-loader]");
        const list = this.drawer.querySelector("[data-availability-list]");
        if (loader) loader.hidden = !isLoading;
        if (list) {
          // Hide previous store cards while a new request is in flight
          if (isLoading) {
            list.hidden = true;
            list.innerHTML = "";
            list.setAttribute("aria-busy", "true");
          } else {
            list.hidden = false;
            list.removeAttribute("aria-busy");
          }
        }
      }

      setError(message) {
        if (!this.drawer) return;
        const errorEl = this.drawer.querySelector("[data-availability-error]");
        if (!errorEl) return;
        if (!message) {
          errorEl.hidden = true;
          errorEl.textContent = "";
          return;
        }
        errorEl.hidden = false;
        errorEl.textContent = message;
      }

      enablePromoButton(enabled) {
        document
          .querySelectorAll(".open-availability-drawer")
          .forEach((button) => {
            button.hidden = !enabled;
            button.style.display = enabled ? "" : "none";
          });
      }

      renderList(rows) {
        if (!this.drawer) return;
        const list = this.drawer.querySelector("[data-availability-list]");
        if (!list) return;

        const labelInStock = this.dataset.labelInStock || "In Stock";
        const labelOutOfStock = this.dataset.labelOutOfStock || "Out of Stock";
        const labelQuantityTpl =
          this.dataset.labelQuantity || "In stock";
          // this.dataset.labelQuantity || "__QTY__ in stock";

        const renderCard = (row) => {
          const statusClass = row.available
            ? "is-available"
            : "is-unavailable";
          const cardClass = row.available
            ? "showroom_available"
            : "showroom_unavailable";
          // Prefer quantity when in stock; fall back to plain "In Stock"
          const statusText = row.available
            ? labelQuantityTpl.replace("__QTY__", String(row.qty)) ||
              labelInStock
            : labelOutOfStock;

          const addressHtml = row.googlelocation
            ? `<a class="pickup-availability-maps-link" href="${escapeHtml(row.googlelocation)}" target="_blank" rel="noopener noreferrer"><address class="pickup-availability-address heading-color mb-10">${escapeHtml(row.address)}</address></a>`
            : `<address class="pickup-availability-address heading-color mb-10">${escapeHtml(row.address)}</address>`;

          return `<li class="pickup-availability-list__item pickup-availability-card ${cardClass}">
              <p class="heading-style fs-18 mb-3">${escapeHtml(row.name)}</p>
              ${addressHtml}
              <p class="pickup-availability-status caption-large my-0 flex align-center ${statusClass}">
                <span class="pickup-availability-status__dot" aria-hidden="true"></span>
                <span>${escapeHtml(statusText)}</span>
              </p>
            </li>`;
        };

        const available = rows.filter((row) => row.available);
        const unavailable = rows.filter((row) => !row.available);
        const separator =
          available.length && unavailable.length
            ? `<li class="pickup-availability-separator" aria-hidden="true"></li>`
            : "";

        list.innerHTML =
          available.map(renderCard).join("") +
          separator +
          unavailable.map(renderCard).join("");
      }
    },
  );
}

if (!customElements.get("pickup-availability-drawer")) {
  customElements.define(
    "pickup-availability-drawer",
    class PickupAvailabilityDrawer extends HTMLElement {
      show() {
        eventModal(this, "open");
      }
    },
  );
}

// Promo / CTA buttons open the drawer; stock is preloaded on page load / variant change
document.addEventListener("click", (event) => {
  const trigger = event.target.closest(".open-availability-drawer");
  if (!trigger) return;

  const host = document.querySelector("pickup-availability[available]");
  const drawer =
    document.querySelector("body > pickup-availability-drawer") ||
    document.querySelector("pickup-availability-drawer");

  if (host) {
    // Only fetch if this variant is not already loaded — avoid spinner on every open
    host.refreshForCurrentVariant();
    host.updateHeader();
  }

  if (!drawer) return;
  drawer.show();
  NextSkyTheme.global.rootToFocus = trigger;
});
